import { Ref, useCallback, useImperativeHandle } from "react";
import { useTranslation } from "@coworker/locales";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import TextModal from "@coworker/apprestructured/src/task-management/components/TextModal/TextModal";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import DateStampNew from "@coworker/app/src/assets/images/DateStampNew.png";

const DATE_STAMP_MAX_LENGTH = 6;

/**
 * Accepts either YYWW (year + week 01–53) or YYMMDD format.
 */
const validateDateStamp = (value: string) =>
  /^(?:\d{2}(0[1-9]|[1-4]\d|5[0-3])|(?:\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])))$/.test(
    value
  );

export type DateStampCreatorFragmentConfigProps = {
  field: string;
};

type DateStampCreatorFragmentProps = {
  configProps: DateStampCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function DateStampCreatorFragment({
  configProps,
  commonProps,
  ref,
}: DateStampCreatorFragmentProps) {
  const { field } = configProps;
  const { blockInput, task, onTaskChange, showValidation } = commonProps;
  const { t } = useTranslation();

  const dateStamp = getTypedValue<string>(getByPath(task, field));
  const valid = !isLoading(dateStamp) && validateDateStamp(dateStamp ?? "");

  useImperativeHandle(ref, () => ({
    valid,
  }));

  const handleConfirmClick = useCallback(
    (newValue: string) => {
      onTaskChange(setByPath(field, newValue));
    },
    [field, onTaskChange]
  );

  if (isLoading(dateStamp)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <TextModal
      title={t("dateStampString")}
      text={dateStamp ?? ""}
      onConfirmClick={handleConfirmClick}
      disabled={blockInput}
      maxLength={DATE_STAMP_MAX_LENGTH}
      inputLabel={t("dateStampString")}
      inputMode="numeric"
      numericOnly
      validateOnConfirm={validateDateStamp}
      validationErrorText={t("invalidDateFormatString")}
      errorMessage={
        showValidation && !valid
          ? dateStamp
            ? t("invalidDateFormatString")
            : t("pleaseFillInString")
          : undefined
      }
      informationTitle={t("whereCanIfindString")}
      informationBody={<FixaImage src={DateStampNew} />}
    />
  );
}

export default DateStampCreatorFragment;
